import {
  ApolloClient,
  ApolloLink,
  defaultDataIdFromObject,
  HttpLink,
  InMemoryCache,
  LazyQueryHookOptions,
  MutationHookOptions,
  OperationVariables,
  QueryHookOptions,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { msalInstance } from "../msalInstance";
import { getAccessToken } from "./authToken";
import { loginRequest } from "../authConfig";

// this is the endpoint for graphql mock api
const MOCK_URL = "/graphql";
// this is a fictive graphql api endpoint to use for demo purposes
const REAL_URL = `${process.env.GATSBY_GAPI_BASE_URL}`;
const httpLink = new HttpLink({ uri: REAL_URL });

// Resolves the bearer token for each request through MSAL (acquireTokenSilent
// returns the cached token if still valid, renews it if expired). This is what
// makes a deleted/invalid token or wiped MSAL cache trigger re-authentication
// on the very next API call instead of waiting for the server to 401.
async function resolveToken(): Promise<string> {
  if (typeof window === "undefined") {
    return "";
  }
  const account = msalInstance.getAllAccounts()[0];
  if (!account) {
    // MSAL has no session at all (storage cleared / signed out).
    localStorage.removeItem("token");
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch {
      // interaction already in progress; the pending redirect wins.
    }
    return "";
  }
  return getAccessToken(msalInstance, account);
}

const authLink = setContext(async (_, { headers }) => {
  const token = await resolveToken();
  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
      "Api-Version": `${process.env.GATSBY_API_VERSION}`,
      "Ocp-Apim-Subscription-Key": `${process.env.GATSBY_HEADER_KEY}`,
    },
  };
});

let loginRedirectInProgress = false;

const errorLink = onError(({ networkError }) => {
  const statusCode = (networkError as { statusCode?: number })?.statusCode;
  if (
    (statusCode !== 401 && statusCode !== 403) ||
    typeof window === "undefined"
  ) {
    return undefined;
  }
  if (loginRedirectInProgress) {
    return undefined;
  }

  loginRedirectInProgress = true;
  void msalInstance.loginRedirect(loginRequest).catch(error => {
    loginRedirectInProgress = false;
    console.error(
      `Unable to redirect to sign-in after an API ${statusCode} response.`,
      error
    );
  });
  return undefined;
});

const link = ApolloLink.from([errorLink, authLink, httpLink]);

// this is an instance of the Apollo Client that uses the 'real' GraphQL API
const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    // dataIdFromObject: o => {
    //   if(o.__typename == 'HotelActiveContactsByHotelId') {
    //     return `HotelActiveContactsByHotelId:${o.id}:${o.contactConnectionId}`;
    //   }
    //   return defaultDataIdFromObject(o);
    // },
    typePolicies: {
      HotelActiveContactsByHotelId: {
        keyFields: ["id", "contactConnectionId"],
      },
    },
  }),
  link: link,
});
// this is an instance of the Apollo Client that uses the mock GraphQL API
const apolloMockClient = new ApolloClient({
  uri: MOCK_URL,
  cache: new InMemoryCache(),
});
// this function return either the mock or real Graphql Apollo client instance to make API calls
export function getApolloClient(useMock = true) {
  return useMock ? apolloMockClient : apolloClient;
}
// this returns the options to be used in the useLazyQuery hook
export function getUseLazyQueryHookOptions(useMock = true) {
  const useThisClient = useMock ? apolloMockClient : apolloClient;
  const options: LazyQueryHookOptions = {
    notifyOnNetworkStatusChange: true,
    client: useThisClient,
  };
  return options;
}
// this returns the options to be used in the useQuery hook
export function getUseQueryHookOptions(
  useMock = true,
  variables: OperationVariables | undefined = undefined
) {
  const useThisClient = useMock ? apolloMockClient : apolloClient;
  const options: QueryHookOptions = {
    notifyOnNetworkStatusChange: true,
    client: useThisClient,
  };
  if (variables) options.variables = variables;
  return options;
}

// this returns the options to be used in the useMutation hook
export function getUseMutationHookOptions(useMock = true) {
  const useThisClient = useMock ? apolloMockClient : apolloClient;
  const options: MutationHookOptions = {
    notifyOnNetworkStatusChange: true,
    client: useThisClient,
  };
  return options;
}
